import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import LeafletMap from '../views/map';
import api from '../api/api';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import img from '../assets/images/avatars/default-avatar.png';
import useSensorData from '../utils/useSensorData';
import './DashboardSupplier.css';

const DashboardSupplier = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const allDataSensor = useSensorData({}) || [];

  const [users, setUsers] = useState([]);
  const [fields, setFields] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [searchName, setSearchName] = useState('');
  const [searchSensorCode, setSearchSensorCode] = useState('');
  const [searchType, setSearchType] = useState('name');
  const [usersLoading, setUsersLoading] = useState(true);
  const [fieldsLoading, setFieldsLoading] = useState(true);
  const [sensorsLoading, setSensorsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [dataFetched, setDataFetched] = useState(false);

  const [mapConfig] = useState({
    zoom: 10,
    center: [36.8, 10.2],
    fromAction: false,
  });

  const [draw] = useState({
    polygon: false,
    circle: false,
    rectangle: false,
    polyline: false,
    marker: false,
    circlemarker: false
  });

  const isValidCoordinate = (lat, lng) => {
    return lat !== null && lat !== undefined && lng !== null && lng !== undefined &&
           !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lng)) &&
           parseFloat(lat) >= -90 && parseFloat(lat) <= 90 &&
           parseFloat(lng) >= -180 && parseFloat(lng) <= 180;
  };

  const fetchData = async () => {
    try {
      const response = await api.get('/supplier/get-users');

      if (response?.data?.users) {
        const usersList = response.data.users;
        setUsers(usersList);
        setUsersLoading(false);

        const processedFields = [];
        const processedSensors = [];

        usersList.forEach(user => {
          user.farms?.forEach(farm => {
            farm.fields?.forEach(field => {
              processedFields.push({
                id: field.id,
                uid: field.uid,
                farmId: field.farm_id,
                name: field.name,
                latitude: field.Latitude,
                longitude: field.Longitude,
                status: field.status,
                sensors: field.sensors || []
              });

              field.sensors?.forEach(sensor => {
                const sensorLat = sensor.Latitude || sensor.latitude;
                const sensorLng = sensor.Longitude || sensor.longitude;

                if (isValidCoordinate(sensorLat, sensorLng)) {
                  processedSensors.push({
                    id: sensor.id,
                    fieldId: sensor.field_id,
                    userId: sensor.user_id,
                    code: sensor.code,
                    latitude: parseFloat(sensorLat),
                    longitude: parseFloat(sensorLng),
                    Latitude: parseFloat(sensorLat),
                    Longitude: parseFloat(sensorLng),
                    lastData: sensor.sensorsData?.[0] || null
                  });
                } else {
                  console.warn(`Capteur ${sensor.code} ignoré: coordonnées invalides`, {
                    lat: sensorLat,
                    lng: sensorLng
                  });
                }
              });
            });
          });
        });

        setFields(processedFields);
        setFieldsLoading(false);
        setSensors(processedSensors);
        setSensorsLoading(false);
        setDataFetched(true);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(t('dashboard_supplier_error', 'Failed to load dashboard data'));
      setUsersLoading(false);
      setFieldsLoading(false);
      setSensorsLoading(false);
    }
  };

  useEffect(() => {
    if (!dataFetched) {
      fetchData();
    }
  }, [dataFetched]);

  const routeToUsers = () => {
    setTimeout(() => {
      navigate('/supplier/users');
    }, 10);
  };

  const routeToSensors = () => {
    setTimeout(() => {
      navigate('/supplier/sensors');
    }, 10);
  };

  const routeToField = (fieldUid, userId) => {
    if (fieldUid) {
      localStorage.setItem("Field", fieldUid);
      navigate(`/Fields/${userId}/${fieldUid}`);
    }
  };
  const goToUserProfile = (userUid) => navigate(`/admin/user/${userUid}`);

  const getLastReadingDate = (sensorCode) => {
    if (!sensorCode || !allDataSensor) return t('no_data_available', 'No data available');

    const sensorData = allDataSensor.find(sensor => sensor?.code === sensorCode);
    return sensorData?.time
      ? moment(sensorData.time).format('MMM Do YYYY, h:mm a')
      : t('no_data_available', 'No data available');
  };

  const getSensorForField = (fieldId) => {
    return sensors.find(sensor => sensor.fieldId === fieldId);
  };

  const filteredFields = fields.filter(field => {
    if (searchType === 'name') {
      return searchName === '' || field.name.toLowerCase().includes(searchName.toLowerCase());
    } else {
      const sensor = getSensorForField(field.id);
      const sensorCode = sensor?.code || '';
      return searchSensorCode === '' || sensorCode.toLowerCase().includes(searchSensorCode.toLowerCase());
    }
  });

  const validSensors = React.useMemo(() => {
    if (!sensors || !Array.isArray(sensors)) {
      console.warn('Sensors is not an array in DashboardSupplier');
      return [];
    }

    return sensors.filter(sensor =>
      sensor &&
      typeof sensor === 'object' &&
      isValidCoordinate(sensor.latitude, sensor.longitude) &&
      isValidCoordinate(sensor.Latitude, sensor.Longitude)
    );
  }, [sensors]);

  const handleSearchTypeChange = (type) => {
    setSearchType(type);
    setSearchName('');
    setSearchSensorCode('');
  };

  return (
    <main className="supplier-dashboard-page">
      <header className="supplier-dashboard-header">
        <span className="supplier-dashboard-eyebrow">SMARTFARM</span>
        <h1>{t('overview')}</h1>
      </header>

      {error && (
        <div className="supplier-empty-state" role="alert">{error}</div>
      )}

      <div className="supplier-dashboard-grid">
        <div>
          <div className="supplier-stats-row">
            <article
              className="supplier-stat-card is-clickable"
              role="button"
              tabIndex={0}
              onClick={routeToUsers}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') routeToUsers(); }}
            >
              <span className="supplier-stat-icon" style={{ backgroundColor: '#e8f7f9', color: '#1d8290' }}>
                <i className="material-icons" aria-hidden="true">people</i>
              </span>
              {usersLoading ? (
                <>
                  <div className="supplier-skeleton" style={{ width: '50%', height: '1.6rem' }} />
                  <div className="supplier-skeleton" style={{ width: '70%', height: '0.9rem' }} />
                </>
              ) : (
                <>
                  <p className="supplier-stat-value">{users.length}</p>
                  <p className="supplier-stat-label">{t('active_users')}</p>
                </>
              )}
            </article>

            <article className="supplier-stat-card">
              <span className="supplier-stat-icon" style={{ backgroundColor: '#fff7e5', color: '#e4a72d' }}>
                <i className="material-icons" aria-hidden="true">landscape</i>
              </span>
              {fieldsLoading ? (
                <>
                  <div className="supplier-skeleton" style={{ width: '50%', height: '1.6rem' }} />
                  <div className="supplier-skeleton" style={{ width: '70%', height: '0.9rem' }} />
                </>
              ) : (
                <>
                  <p className="supplier-stat-value">{fields.length}</p>
                  <p className="supplier-stat-label">{t('active_fields')}</p>
                </>
              )}
            </article>

            <article
              className="supplier-stat-card is-clickable"
              role="button"
              tabIndex={0}
              onClick={routeToSensors}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') routeToSensors(); }}
            >
              <span className="supplier-stat-icon" style={{ backgroundColor: '#e8f7fb', color: '#20aeca' }}>
                <i className="material-icons" aria-hidden="true">sensors</i>
              </span>
              {sensorsLoading ? (
                <>
                  <div className="supplier-skeleton" style={{ width: '50%', height: '1.6rem' }} />
                  <div className="supplier-skeleton" style={{ width: '70%', height: '0.9rem' }} />
                </>
              ) : (
                <>
                  <p className="supplier-stat-value">{sensors.length}</p>
                  <p className="supplier-stat-label">{t('active_sensors')}</p>
                </>
              )}
            </article>
          </div>

          <section className="supplier-card">
            <div className="supplier-card-header">
              <h3 className="supplier-card-title">
                <i className="material-icons" aria-hidden="true">landscape</i>
                {t('total_fields', 'Total Fields')}
              </h3>
              <div className="supplier-search-controls">
                <div className="supplier-search-toggle" role="group" aria-label={t('search_by', 'Search by')}>
                  <button
                    type="button"
                    className={`supplier-toggle-btn ${searchType === 'name' ? 'is-active' : ''}`}
                    aria-pressed={searchType === 'name'}
                    onClick={() => handleSearchTypeChange('name')}
                  >
                    {t('search_by_name', 'Search by Name')}
                  </button>
                  <button
                    type="button"
                    className={`supplier-toggle-btn ${searchType === 'sensor' ? 'is-active' : ''}`}
                    aria-pressed={searchType === 'sensor'}
                    onClick={() => handleSearchTypeChange('sensor')}
                  >
                    {t('search_by_sensor_code', 'Search by Sensor Code')}
                  </button>
                </div>
                {searchType === 'name' ? (
                  <input
                    className="supplier-search-input"
                    type="search"
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    placeholder={t('search_fields_by_name', 'Search fields by name…')}
                    aria-label={t('search_fields_by_name', 'Search fields by name…')}
                  />
                ) : (
                  <input
                    className="supplier-search-input"
                    type="search"
                    value={searchSensorCode}
                    onChange={(e) => setSearchSensorCode(e.target.value)}
                    placeholder={t('search_fields_by_sensor_code', 'Search fields by sensor code…')}
                    aria-label={t('search_fields_by_sensor_code', 'Search fields by sensor code…')}
                  />
                )}
              </div>
            </div>
            <div className="supplier-card-body">
              {fieldsLoading ? (
                [1, 2].map((item) => (
                  <div key={item} className="supplier-skeleton" style={{ height: '10rem', width: '100%', marginBottom: '0.85rem' }} />
                ))
              ) : filteredFields.length > 0 ? (
                filteredFields.map((field) => {
                  const sensor = getSensorForField(field.id);
                  const sensorCode = sensor?.code || '';
                  const userId = sensor?.userId || '';

                  return (
                    <div className="supplier-field-card" key={field.uid}>
                      <div className="supplier-field-header">
                        <h4>{field.name}</h4>
                        <p>{t('sensor_code')}: {sensorCode || t('not_applicable', 'N/A')}</p>
                      </div>

                      <div className="supplier-progress-section">
                        <div className="supplier-progress-level">
                          <p className="supplier-progress-label">{t('niveau')} 1</p>
                          <div className="supplier-progress-bar-container">
                            <div className="supplier-progress-marker" style={{ left: '20%' }} />
                          </div>
                        </div>
                        <div className="supplier-progress-level">
                          <p className="supplier-progress-label">{t('niveau')} 2</p>
                          <div className="supplier-progress-bar-container">
                            <div className="supplier-progress-marker" style={{ left: '60%' }} />
                          </div>
                        </div>
                        <div className="supplier-progress-level">
                          <p className="supplier-progress-label">{t('niveau')} 3</p>
                          <div className="supplier-progress-bar-container">
                            <div className="supplier-progress-marker" style={{ left: '80%' }} />
                          </div>
                        </div>
                        <div className="supplier-progress-status">
                          <span>{t('refill', 'Refill')}</span>
                          <span className="is-optimal">{t('Optimal')}</span>
                          <span>{t('full', 'Full')}</span>
                        </div>
                      </div>

                      <div className="supplier-field-footer">
                        <div className="supplier-last-reading">
                          <strong>{t('last_reading', 'Last Reading')}:</strong> {getLastReadingDate(sensorCode)}
                        </div>
                        <button
                          type="button"
                          className="supplier-see-all-btn"
                          onClick={() => routeToField(field.uid, userId)}
                        >
                          {t('see_all')}
                        </button>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="supplier-empty-state">{t('no_fields_found', 'No fields found')}</p>
              )}
            </div>
          </section>
        </div>

        <div>
          <div className="supplier-map-container">
            {sensorsLoading ? (
              <div className="supplier-map-loading">
                <span role="status">{t('loading_map', 'Loading map…')}</span>
              </div>
            ) : validSensors.length > 0 ? (
              <LeafletMap
                key="dashboard-map-static"
                sensor={validSensors}
                draw={draw}
                zoom={mapConfig.zoom}
                center={mapConfig.center}
                fromAction={mapConfig.fromAction}
                initialCenter={[36.806389, 10.181667]}
                initialZoom={12}
                mapId="supplier-dashboard-map"
              />
            ) : (
              <div className="supplier-map-empty">
                <span>{t('no_sensors_with_coordinates', 'No sensors with valid coordinates found')}</span>
              </div>
            )}
          </div>

          <section className="supplier-card">
            <div className="supplier-card-header">
              <h3 className="supplier-card-title">
                <i className="material-icons" aria-hidden="true">people</i>
                {t('all_customers', 'All Customers')}
              </h3>
            </div>
            <div className="supplier-card-body" style={{ maxHeight: '18.75rem', overflowY: 'auto' }}>
              {usersLoading ? (
                [1, 2, 3].map((item) => (
                  <div key={item} className="supplier-customer-item">
                    <div className="supplier-skeleton" style={{ width: '2.6rem', height: '2.6rem', borderRadius: '50%' }} />
                    <div className="supplier-skeleton" style={{ width: '60%', height: '0.9rem' }} />
                  </div>
                ))
              ) : users.length > 0 ? (
                users.map((user, index) => (
                  <div key={user.uid || index} className="supplier-customer-item">
                    <img
                      className="supplier-customer-avatar"
                      src={user.upload_file_name
                        ? `${process.env.REACT_APP_BASE_URL}/static/${user.upload_file_name}`
                        : img
                      }
                      alt={user.name}
                      onError={(e) => { e.target.src = img; }}
                    />
                    <button
                      type="button"
                      className="supplier-customer-name"
                      onClick={() => goToUserProfile(user.uid)}
                    >
                      {user.name}
                    </button>
                  </div>
                ))
              ) : (
                <p className="supplier-empty-state">{t('no_customers_found', 'No customers found')}</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </main>
  );
};

export default DashboardSupplier;
